/**
 * 표준 양식 — **직접 서명 양식의 출발점.** (docs/decisions/0221)
 *
 * 고용노동부 표준근로계약서(공공저작물)의 조항을 옮겼다. 회사는 이것을 고르고 **고쳐서**
 * 자기 양식으로 저장한다 — 여기 있는 것은 저장되지 않는 출발점이고, 표에 들어가는 순간 그
 * 회사의 것이 된다. 그래서 이 파일을 고쳐도 이미 만든 양식은 안 바뀐다(바뀌면 안 된다:
 * 그 양식으로 이미 서명받은 계약이 있다).
 *
 * ─── 빈칸이 자리표시가 된 방식 ───
 * 원본 서식의 밑줄(`____`)은 둘로 갈렸다:
 *   - 우리 표에 그 값이 있으면 채우기 출처(`{{employee.name}}` · `{{employee.wage}}`)
 *   - 없으면 입력칸(`{{input.place}}`) — 요청하는 사람이 그때 적는다
 * 조항 번호("1. 근로계약기간")는 **적지 않는다** — 그리는 쪽(`renderESignForm`)이 붙인다. 적어 두면
 * 회사가 조항 순서를 바꾸는 순간 번호가 틀린다.
 * 체크박스("있음 ( ) 없음 ( )")는 입력칸 하나가 됐다. 체크를 칸으로 그리면 서명하는 쪽이
 * 체크할 수 있어야 하는데, 근로조건은 **사업주가 정해서 제시하는 것**이다.
 *
 * ─── 조항 문구 ───
 * 원본을 바꾸지 않았다. 바꾼 것은 밑줄을 자리표시로 바꾼 것뿐이다 — 법정 기재사항(근로기준법
 * 17조: 임금 · 소정근로시간 · 휴일 · 연차유급휴가)이 각 양식에 다 있는지 시험이 본다.
 */
import type { ESignFormClause, ESignFormInput, ESignFormPreset } from './esign.js';

/* ─── 공통 조항 ─────────────────────────────────────────────────────────────── */

const opening: ESignFormClause = {
  title: null,
  body: '{{company.legalName}}(이하 "사업주"라 함)과(와) {{employee.name}}(이하 "근로자"라 함)은 다음과 같이 근로계약을 체결한다.',
};

const delivery: ESignFormClause = {
  title: '근로계약서 교부',
  body: '사업주는 근로계약을 체결함과 동시에 본 계약서를 사본하여 근로자의 교부요구와 관계없이 근로자에게 교부함(근로기준법 제17조 이행)',
};

const faithfulness: ESignFormClause = {
  title: '근로계약, 취업규칙 등의 성실한 이행의무',
  body: '사업주와 근로자는 각자가 근로계약, 취업규칙, 단체협약을 지키고 성실하게 이행하여야 함',
};

const others: ESignFormClause = {
  title: '기타',
  body: '이 계약에 정함이 없는 사항은 근로기준법령에 의함',
};

const insurance: ESignFormClause = {
  title: '사회보험 적용여부',
  body: '{{input.insurance}}',
};

/**
 * 임금 조항. 시급·월급은 **등록 급여**에서 온다(`employee.wage` — 계약 시작일에 유효한 것, 0032).
 * 등록하지 않았으면 빈칸으로 나가고, 요청 화면이 그 사실을 미리 말한다.
 */
const wage = (extra: readonly string[] = []): ESignFormClause => ({
  title: '임금',
  body: [
    '- 임금({{employee.salaryType}}) : {{employee.wage}}원',
    '- 상여금 : {{input.bonus}}',
    '- 기타급여(제수당 등) : {{input.allowance}}',
    ...extra,
    '- 임금지급일 : 매월 {{input.payday}}일(휴일의 경우는 전일 지급)',
    '- 지급방법 : {{input.payMethod}}',
  ].join('\n'),
});

/** 입력칸 중 세 양식이 다 쓰는 것. 기본값은 대부분의 가게에서 그대로 쓰는 값이다. */
const commonInputs: readonly ESignFormInput[] = [
  { key: 'place', label: '근무장소', defaultValue: null },
  { key: 'duty', label: '업무의 내용', defaultValue: null },
  { key: 'bonus', label: '상여금', defaultValue: '없음' },
  { key: 'allowance', label: '기타급여(제수당 등)', defaultValue: '없음' },
  { key: 'payday', label: '임금지급일(매월 며칠)', defaultValue: '10' },
  { key: 'payMethod', label: '지급방법', defaultValue: '근로자 명의 예금통장에 입금' },
  { key: 'insurance', label: '사회보험', defaultValue: '고용보험, 산재보험, 국민연금, 건강보험' },
];

/* ─── 양식 셋 ───────────────────────────────────────────────────────────────── */

/** 정규직 — 기간의 정함이 없는 경우. */
const indefinite: ESignFormPreset = {
  key: 'standard.indefinite',
  name: '표준근로계약서(기간의 정함이 없는 경우)',
  contractType: 'laborer',
  body: {
    documentTitle: '표준근로계약서',
    clauses: [
      opening,
      { title: '근로개시일', body: '{{contract.startDate}}부터' },
      { title: '근무장소', body: '{{input.place}}' },
      { title: '업무의 내용', body: '{{input.duty}}' },
      { title: '소정근로시간', body: '{{input.hours}} (휴게시간 : {{input.breakTime}})' },
      { title: '근무일/휴일', body: '매주 {{input.workDays}} 근무, 주휴일 매주 {{input.weeklyHoliday}}' },
      wage(),
      { title: '연차유급휴가', body: '연차유급휴가는 근로기준법에서 정하는 바에 따라 부여함' },
      insurance,
      delivery,
      faithfulness,
      others,
    ],
    inputs: [
      ...commonInputs.slice(0, 2),
      { key: 'hours', label: '소정근로시간', defaultValue: '09:00부터 18:00까지' },
      { key: 'breakTime', label: '휴게시간', defaultValue: '12:00 ~ 13:00' },
      { key: 'workDays', label: '근무일', defaultValue: '5일(월~금)' },
      { key: 'weeklyHoliday', label: '주휴일', defaultValue: '일요일' },
      ...commonInputs.slice(2),
    ],
  },
};

/** 계약직 — 기간의 정함이 있는 경우. 정규직과 다른 것은 첫 조항 하나다. */
const fixedTerm: ESignFormPreset = {
  key: 'standard.fixedTerm',
  name: '표준근로계약서(기간의 정함이 있는 경우)',
  contractType: 'laborer',
  body: {
    ...indefinite.body,
    clauses: [
      opening,
      { title: '근로계약기간', body: '{{contract.startDate}}부터 {{contract.endDate}}까지' },
      ...indefinite.body.clauses.slice(2),
    ],
  },
};

/**
 * 단시간근로자 — **아르바이트의 계약서다.** 이 제품의 주 고객(사장 + 알바)이 가장 많이 쓸 양식이라
 * 표준 양식 목록의 맨 앞에 둔다.
 *
 * 다른 둘과 다른 점이 법에서 온다: 근로일마다 시간이 다를 수 있어 그것을 적는 칸이 있고
 * (기간제법 17조), 정한 시간을 넘긴 근로에는 법정 근로시간 안이라도 가산임금이 붙는다
 * (같은 법 6조 3항). 연차는 통상근로자의 근로시간에 비례한다.
 */
const partTime: ESignFormPreset = {
  key: 'standard.partTime',
  name: '단시간근로자 표준근로계약서(아르바이트)',
  contractType: 'laborer',
  body: {
    documentTitle: '단시간근로자 표준근로계약서',
    clauses: [
      opening,
      { title: '근로계약기간', body: '{{contract.startDate}}부터 {{contract.endDate}}까지' },
      { title: '근무장소', body: '{{input.place}}' },
      { title: '업무의 내용', body: '{{input.duty}}' },
      { title: '근로일 및 근로일별 근로시간', body: '{{input.schedule}}\n- 주휴일 : 매주 {{input.weeklyHoliday}}' },
      wage([
        '- 초과근로에 대한 가산임금률 : 통상임금의 100분의 50 이상(근로하기로 정한 시간을 초과하여 근로하면 법정 근로시간 내라도 지급)',
      ]),
      { title: '연차유급휴가', body: '통상근로자의 근로시간에 비례하여 연차유급휴가 부여' },
      insurance,
      delivery,
      faithfulness,
      others,
    ],
    inputs: [
      ...commonInputs.slice(0, 2),
      {
        key: 'schedule',
        label: '근로일과 근로시간',
        defaultValue: '월·수·금 10:00 ~ 15:00 (휴게 12:00 ~ 12:30)',
      },
      { key: 'weeklyHoliday', label: '주휴일', defaultValue: '일요일' },
      ...commonInputs.slice(2),
    ],
  },
};

/** 화면의 순서가 곧 이 순서다 — 알바가 맨 앞이다(위 주석). */
export const ESIGN_FORM_PRESETS: readonly ESignFormPreset[] = [partTime, indefinite, fixedTerm];

export const esignFormPresetOf = (key: string): ESignFormPreset | null =>
  ESIGN_FORM_PRESETS.find((p) => p.key === key) ?? null;
